import React, { useState, useEffect } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useFilters,
  useGlobalFilter,
} from "react-table";
import axiosClient from "../../services/apiClient";
import {
  getSensorSessions,
  getSensorPresence,
  getSensorConnectivity,
  getSensorStateChanges,
  getPCOStateChanges,
  getBootInfo,
  getSensors,
  getSensorAlarms,
  countAlertTransitions,
  countAlarmShutoffs,
  deriveStoveControl,
  calcTotalSessionsDuration,
  calcSessionPresenceDuration,
  calcSessionExposedDuration,
  calcSessionsTooHotPotDuration,
  calcSessionPresenceCount,
  calcSessionFireCount,
} from "./unitEventsUtils";
import { computeSensorTrend, filterSessionsByWindow } from "./trendUtils";
import TrendBadge from "../../components/ui/TrendBadge";
import PriorityBadge from "../../components/ui/PriorityBadge";
import SensorAttention from "./SensorAttention";
import { LuCirclePower, LuPower, LuPowerOff } from "react-icons/lu";
import { IoMdRefreshCircle } from "react-icons/io";
import { FaUnlock, FaLock } from "react-icons/fa";
import {
  IoNotifications,
  IoCloudOffline,
} from "react-icons/io5";
import { IconContext } from "react-icons";
import { PiCookingPotFill } from "react-icons/pi";
import { useQuery, useQueries } from "@tanstack/react-query";
import Spinner from "../../components/ui/Spinner";
import Button from "../../components/ui/Button";
import StatusDot from "../../components/ui/StatusDot";
import ErrorState from "../../components/ui/ErrorState";
import SortIndicator from "../../components/ui/SortIndicator";
import BuildingScheduleModal from "../../components/ui/BuildingScheduleModal";
import StoveStateBadge from "../../components/ui/StoveStateBadge";
import { reducePcoState } from "../../services/cookingState";
import { useInsightsCtx } from "./InsightsLayout";
import { FaRegCalendarAlt } from "react-icons/fa";


const delay = (ms) => new Promise((res) => setTimeout(res, ms));

/**
 * Alert-count gradient: 0 is muted; small numbers are yellow, ramping through
 * orange to red as the count grows.
 */
function alertCountColor(v) {
  if (v <= 0) return "text-ink-3";
  return ""; // colored values use inline style below
}

function alertCountStyle(v) {
  if (v <= 0) return undefined;
  if (v < 2) return { color: "#F1C40F" }; // yellow (1)
  if (v < 5) return { color: "#FB8C00" }; // orange (2-4)
  if (v < 10) return { color: "#E74C3C" }; // red (5-9)
  return { color: "#B00020" }; // deep red (≥10)
}

const SHORT_WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const SHORT_MONTH = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Tailwind text color for how stale a "last X" timestamp is. Returns a
 *  warning tone after `warnDays` and a critical tone after `criticalDays`.
 *  Used to flag residents who have not cooked or been present in a long time
 *  so the row visually stands out. */
function stalenessColor(ts, { warnDays = 3, criticalDays = 7 } = {}) {
  if (ts == null || Number.isNaN(ts)) return "text-ink-3";
  const days = (Date.now() - ts) / (24 * 60 * 60_000);
  if (days >= criticalDays) return "text-alert-critical font-semibold";
  if (days >= warnDays) return "text-alert-warning font-semibold";
  return "";
}

/** Parse an ISO 8601-ish string as UTC. Telemetry DTOs are inconsistent: some
 *  events (connectivity) serialize with a trailing Z, others (state change,
 *  presence) do not. Plain Date.parse() treats no-Z strings as browser-local
 *  time, which silently shifts every liveness comparison by the browser's UTC
 *  offset. Normalize by appending Z when the string carries no timezone
 *  designator. Returns null for missing/invalid input. */
function parseUtc(raw) {
  if (!raw) return null;
  let s = String(raw).replace(" ", "T");
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s += "Z";
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : t;
}

/** Compact relative timestamp for table cells: "5 min", "3 hr", "Yesterday",
 *  weekday name within the past week, "Apr 14" within the year, or
 *  "Apr 14, 2025" for older. Returns "-" for null/invalid input. */
function formatRelativeShort(ts) {
  if (ts == null || Number.isNaN(ts)) return "-";
  const now = Date.now();
  const diff = now - ts;
  if (diff < 0) return "just now";
  if (diff < 60_000) return "just now";
  if (diff < 60 * 60_000) {
    const m = Math.round(diff / 60_000);
    return `${m} min`;
  }
  if (diff < 24 * 60 * 60_000) {
    const h = Math.round(diff / (60 * 60_000));
    return `${h} hr`;
  }
  const d = new Date(ts);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startOfDay = new Date(d);
  startOfDay.setHours(0, 0, 0, 0);
  const dayDiff = Math.round(
    (today.getTime() - startOfDay.getTime()) / 86400000
  );
  if (dayDiff === 1) return "Yesterday";
  if (dayDiff < 7) return SHORT_WEEKDAY[d.getDay()];
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const md = `${SHORT_MONTH[d.getMonth()]} ${d.getDate()}`;
  return sameYear ? md : `${md}, ${d.getFullYear()}`;
}

export default function UnitEventsTable(props) {
  const [TableData, setTableData] = useState([]);
  const [tableUpdateFlag, setTableUpdateFlag] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingMsg, setPendingMsg] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const { tempUnit } = useInsightsCtx();

  // Per-sensor session pull. Bumped from 100 because chatty kitchens were
  // hitting the cap and showing artificially low Sessions counts.
  const sensorPageSize = 1000;
  const refetchInterval = 60000 * 60; // refetch every hour

  const {
    data: sensorResults,
    error: sensorsError,
    isPending: sensorsLoading,
  } = useQuery({
    queryKey: ["sensors", props.building.buildingId, tableUpdateFlag],
    queryFn: async () => await getSensors(props.building.buildingId),
    refetchInterval,
  });

  const sensors = !sensorsLoading && !sensorsError ? sensorResults.data : [];

  const { sessions, sensorSessionLoading, sensorSessionError } = useQueries({
    queries: sensors.map((sensor) => ({
      queryKey: [
        "sessions",
        sensor.name,
        sensorPageSize,
        tableUpdateFlag,
        props.startDate,
        props.endDate,
      ],
      queryFn: async () =>
        await getSensorSessions(
          sensor.name,
          sensorPageSize,
          props.startDate,
          props.endDate
        ),
      enabled: !!sensor.name,
      refetchInterval,
    })),
    combine: (results) => ({
      sessions: results
        ? results.map((result) => (result.data ? result.data.data : []))
        : [],
      sensorSessionLoading: results
        ? results.some((result) => result.isPending)
        : true,
      sensorSessionError: results
        ? results.some((result) => result.isError)
        : false,
    }),
    enabled: sensors?.length > 0,
  });

  const { connectivity, connectivityLoading, connectivityError } = useQueries({
    queries: sensors.map((sensor) => ({
      queryKey: ["connectivity", sensor.name, sensorPageSize, tableUpdateFlag],
      queryFn: async () =>
        await getSensorConnectivity(sensor.name, sensorPageSize),
      enabled: !!sensor.name,
      refetchInterval,
    })),
    combine: (results) => ({
      connectivity: results
        ? results.map((result) => (result.data ? result.data.data : []))
        : [],
      connectivityLoading: results
        ? results.some((result) => result.isPending)
        : true,
      connectivityError: results
        ? results.some((result) => result.isError)
        : false,
    }),
    enabled: sensors?.length > 0,
  });

  const { statechanges, statechangesLoading, statechangesError } = useQueries({
    queries: sensors.map((sensor) => ({
      queryKey: ["statechanges", sensor.name, sensorPageSize, tableUpdateFlag],
      queryFn: async () =>
        await getSensorStateChanges(sensor.name, sensorPageSize),
      enabled: !!sensor.name,
      refetchInterval,
    })),
    combine: (results) => ({
      statechanges: results
        ? results.map((result) => (result.data ? result.data.data : []))
        : [],
      statechangesLoading: results
        ? results.some((result) => result.isPending)
        : true,
      statechangesError: results
        ? results.some((result) => result.isError)
        : false,
    }),
    enabled: sensors?.length > 0,
  });

  const { PCOstatechanges, PCOstatechangesLoading, PCOstatechangesError } =
    useQueries({
      queries: sensors.map((sensor) => ({
        queryKey: [
          "PCOstatechanges",
          sensor.name,
          sensorPageSize,
          tableUpdateFlag,
          props.startDate,
          props.endDate,
        ],
        queryFn: async () =>
          await getPCOStateChanges(
            sensor.name,
            sensorPageSize,
            props.startDate,
            props.endDate
          ),
        enabled: !!sensor.name,
        refetchInterval,
      })),
      combine: (results) => ({
        PCOstatechanges: results
          ? results.map((result) => (result.data ? result.data.data : []))
          : [],
        PCOstatechangesLoading: results
          ? results.some((result) => result.isPending)
          : true,
        PCOstatechangesError: results
          ? results.some((result) => result.isError)
          : false,
      }),
      enabled: sensors?.length > 0,
    });

  const {
    currentPCOstatechanges,
    currentPCOstatechangesLoading,
    currentPCOstatechangesError,
  } = useQueries({
    queries: sensors.map((sensor) => ({
      queryKey: [
        "currentPCOstatechanges",
        sensor.name,
        sensorPageSize,
        tableUpdateFlag,
      ],
      queryFn: async () =>
        await getPCOStateChanges(sensor.name, sensorPageSize),
      enabled: !!sensor.name,
      refetchInterval,
    })),
    combine: (results) => ({
      currentPCOstatechanges: results
        ? results.map((result) => (result.data ? result.data.data : []))
        : [],
      currentPCOstatechangesLoading: results
        ? results.some((result) => result.isPending)
        : true,
      currentPCOstatechangesError: results
        ? results.some((result) => result.isError)
        : false,
    }),
    enabled: sensors?.length > 0,
  });

  const { bootinfo, bootinfoLoading, bootinfoError } = useQueries({
    queries: sensors.map((sensor) => ({
      queryKey: ["bootinfo", sensor.name, sensorPageSize, tableUpdateFlag],
      queryFn: async () => await getBootInfo(sensor.name, sensorPageSize),
      enabled: !!sensor.name,
      refetchInterval,
    })),
    combine: (results) => ({
      bootinfo: results
        ? results.map((result) => (result.data ? result.data.data : []))
        : [],
      bootinfoLoading: results
        ? results.some((result) => result.isPending)
        : true,
      bootinfoError: results
        ? results.some((result) => result.isError)
        : false,
    }),
    enabled: sensors?.length > 0,
  });

  const { alertRecords, alertsLoading, alertsError } = useQueries({
    // Shared cache key with SensorDetailPage + useBuildingAggregates so the
    // three views agree on a single fetch per sensor. tableUpdateFlag is
    // intentionally NOT in the key; use queryClient.invalidateQueries to
    // force a refresh after a control action.
    queries: sensors.map((sensor) => ({
      queryKey: ["alerts-agg", sensor.name],
      queryFn: async () => await getSensorAlarms(sensor.name, 5000),
      enabled: !!sensor.name,
      refetchInterval,
    })),
    combine: (results) => ({
      alertRecords: results
        ? results.map((result) => (result.data ? result.data.data : []))
        : [],
      alertsLoading: results ? results.some((r) => r.isPending) : true,
      alertsError: results ? results.some((r) => r.isError) : false,
    }),
    enabled: sensors?.length > 0,
  });

  // Full-history session pull (no date filter) powering the trend column. Shares
  // the SensorDetailPage's `sessions-history` cache key so we don't double-fetch.
  const { historySessions, historySessionsLoading } = useQueries({
    queries: sensors.map((sensor) => ({
      queryKey: ["sessions-history", sensor.name],
      queryFn: async () => await getSensorSessions(sensor.name, 1000, null, null),
      enabled: !!sensor.name,
      staleTime: 60_000,
      refetchInterval,
    })),
    combine: (results) => ({
      historySessions: results
        ? results.map((result) => (result.data ? result.data.data : []))
        : [],
      historySessionsLoading: results
        ? results.some((r) => r.isPending)
        : true,
    }),
    enabled: sensors?.length > 0,
  });

  // Last cooking session per sensor: pageSize=1 (no date filter) so we
  // always see the truly-last one, regardless of the page-level picker.
  const { lastSessionTimestamps, lastSessionCreatedTs } = useQueries({
    queries: sensors.map((sensor) => ({
      queryKey: ["last-session-row", sensor.name],
      queryFn: async () =>
        await getSensorSessions(sensor.name, 1, null, null),
      enabled: !!sensor.name,
      staleTime: 60_000,
    })),
    combine: (results) => ({
      lastSessionTimestamps: results
        ? results.map((r) => parseUtc(r?.data?.data?.[0]?.dateStart))
        : [],
      lastSessionCreatedTs: results
        ? results.map((r) => parseUtc(r?.data?.data?.[0]?.dateCreated))
        : [],
    }),
    enabled: sensors?.length > 0,
  });

  // Last presence per sensor: same idea, single record from /presence.
  const { lastPresenceTimestamps, lastPresenceCreatedTs } = useQueries({
    queries: sensors.map((sensor) => ({
      queryKey: ["last-presence", sensor.name],
      queryFn: async () => await getSensorPresence(sensor.name, 1),
      enabled: !!sensor.name,
      staleTime: 60_000,
    })),
    combine: (results) => ({
      lastPresenceTimestamps: results
        ? results.map((r) => parseUtc(r?.data?.data?.[0]?.datetime))
        : [],
      lastPresenceCreatedTs: results
        ? results.map((r) => parseUtc(r?.data?.data?.[0]?.dateCreated))
        : [],
    }),
    enabled: sensors?.length > 0,
  });

  const error =
    sensorsError ||
    sensorSessionError ||
    connectivityError ||
    statechangesError ||
    PCOstatechangesError ||
    bootinfoError ||
    currentPCOstatechangesError ||
    alertsError;

  useEffect(() => {
    if (
      sensorsLoading ||
      sensorSessionLoading ||
      connectivityLoading ||
      statechangesLoading ||
      PCOstatechangesLoading ||
      bootinfoLoading ||
      currentPCOstatechangesLoading ||
      alertsLoading ||
      historySessionsLoading
    ) {
      return;
    }

    const result = [];
    for (let i = 0; i < sensors.length; i++) {
      const sensor = sensors[i];
      const sensorSessions = sessions[i];
      const sensorConnectivity = connectivity[i];
      const sensorStateChanges = statechanges[i];
      const sensorPCOStateChanges = PCOstatechanges[i];
      const currentPCOstatechange = currentPCOstatechanges[i];
      const sensorBootInfo = bootinfo[i];
      const sensorAlertRecords = alertRecords[i] ?? [];
      const sensorHistorySessions = historySessions[i] ?? [];

      let status = "disconnected";
      // Prefer dateCreated (server-ingest, always UTC, immune to device clock
      // skew) for the liveness comparison. Fall back to the event datetime if
      // the record predates the ingest-stamp rollout.
      let latestConnEventTs = null;
      if (sensorConnectivity.length > 0 && sensorConnectivity[0]) {
        status = sensorConnectivity[0].eventType;
        latestConnEventTs =
          parseUtc(sensorConnectivity[0].dateCreated) ??
          parseUtc(sensorConnectivity[0].datetime);
      }
      status = status === "connected" ? "Online" : "Offline";

      // Sensors publish "disconnected" via LWT on ungraceful drops but do
      // not always publish "connected" on reconnect, so the latest connectivity
      // event can be a stale disconnect. If any other telemetry post-dates it,
      // the sensor is talking to us again — treat as Online. All timestamps
      // here are compared on the same server-ingest UTC basis, which also
      // sidesteps device clock skew (a sensor with an unset RTC could stamp
      // future or 1970-era events).
      //
      // A sensor with no connectivity record at all skips this branch and
      // stays Offline; without a disconnect reference we cannot judge which
      // side of the last drop the telemetry falls on.
      if (status === "Offline" && latestConnEventTs != null) {
        const ingestOr = (rec) =>
          parseUtc(rec?.dateCreated) ?? parseUtc(rec?.datetime);
        const otherTs = [
          ingestOr(sensorStateChanges[0]),
          ingestOr(currentPCOstatechange[0]),
          lastPresenceCreatedTs[i] ?? lastPresenceTimestamps[i] ?? null,
          lastSessionCreatedTs[i] ?? lastSessionTimestamps[i] ?? null,
        ].filter((n) => typeof n === "number");
        const newestOther = otherTs.length ? Math.max(...otherTs) : null;
        if (newestOther != null && newestOther > latestConnEventTs) {
          status = "Online";
        }
      }

      let stoveStatus = "NULL";
      if (sensorStateChanges.length > 0 && sensorStateChanges[0]) {
        const s = sensorStateChanges[0];
        stoveStatus =
          s.currentStateEnum === 6 || s.currentStateEnum === 9 ? "OFF" : "ON";
      }

      let isStoveInUse = "NULL";
      if (sensorStateChanges.length > 0 && sensorStateChanges[0]) {
        const s = sensorStateChanges[0];
        if (
          s.prevStateEnum === 0 &&
          (s.currentStateEnum === 1 ||
            s.currentStateEnum === 2 ||
            s.currentStateEnum === 3 ||
            s.currentStateEnum === 4)
        ) {
          isStoveInUse = "YES";
        } else {
          isStoveInUse = "NO";
        }
      }

      let isPCOconnected = "NULL";
      let stoveCurrent = "NULL";
      let PCOFWVersion = "NULL";
      let IsStoveRemoteUnlocked = "NULL";

      // PCO connection state comes from the latest PCO state-change record
      // (no date filter). Mirrors the mobile app's reliable detection:
      // empty data or currentUnitConnectedStatus === 0 → no PCO attached.
      const currentPCO = currentPCOstatechange[0];
      if (currentPCO) {
        PCOFWVersion = `${currentPCO.currentFWverMajor}.${currentPCO.currentFWverMinor}.${currentPCO.currentFWverPatch}`;
        IsStoveRemoteUnlocked = currentPCO.currentIsStoveRemoteUnlocked
          ? "UNLOCKED"
          : "LOCKED";
        isPCOconnected =
          currentPCO.currentUnitConnectedStatus !== 0 ? "YES" : "NO";
      } else {
        // No PCO state-change history at all → no PCO attached.
        isPCOconnected = "NO";
      }

      // Stove current is a windowed reading; only set it from the
      // date-filtered query so it reflects activity in the picker range.
      const filteredPCO = sensorPCOStateChanges[0];
      if (filteredPCO) {
        stoveCurrent = `${filteredPCO.currentCurrentmA}`;
      }

      let SCSFWVersion = "NULL";
      if (sensorBootInfo.length > 0 && sensorBootInfo[0]) {
        SCSFWVersion = sensorBootInfo[0].firmwareVersion;
      }

      // Defensive: the sessions endpoint's date filter can't be fully trusted
      // (some telemetry endpoints ignore date params and return all history up to
      // pageSize, which inflates the count). Re-filter to the selected window by
      // each session's start time so all session metrics reflect the range.
      const windowSessions =
        props.startDate && props.endDate
          ? filterSessionsByWindow(
              sensorSessions,
              props.startDate,
              props.endDate
            )
          : sensorSessions;

      const totalDuration = calcTotalSessionsDuration(windowSessions);

      const trend = computeSensorTrend(
        sensorAlertRecords,
        sensorHistorySessions,
        new Date(),
        { offlineNorm: status === "Offline" ? 1 : 0 }
      );

      result.push({
        UnitNumber: sensor.displayName,
        SensorID: sensor.name,
        pcoStateReduced: reducePcoState(currentPCO),
        LastSessionTs: lastSessionTimestamps[i] ?? null,
        LastPresenceTs: lastPresenceTimestamps[i] ?? null,
        Status: status,
        OfflineSinceTs:
          status === "Offline" ? latestConnEventTs ?? null : null,
        stoveStatus,
        isStoveInUse,
        isPCOconnected,
        stoveCurrent,
        PCOFWVersion,
        SCSFWVersion,
        IsStoveRemoteUnlocked,
        NumberOfSessions: windowSessions.length,
        AvgSessionLength: windowSessions.length
          ? Math.round((totalDuration / windowSessions.length) * 100) / 100
          : 0,
        MaxSessionIntensity: 0,
        Warnings: 0,
        Alerts: countAlertTransitions(
          sensorAlertRecords,
          props.startDate,
          props.endDate
        ),
        Shutdowns: countAlarmShutoffs(
          sensorAlertRecords,
          props.startDate,
          props.endDate
        ),
        Trend: trend.status,
        TrendRank: trend.rank,
        TrendLevel: trend.level,
        PriorityScore: trend.priorityScore,
        PriorityBand: trend.priorityBand,
        PresenceCounter: calcSessionPresenceCount(windowSessions),
        PresenceRatio:
          Math.round(
            (calcSessionPresenceDuration(windowSessions) / totalDuration) * 100
          ) / 100,
        ExposedBurnerAccTime: calcSessionExposedDuration(windowSessions),
        TooHotAccTime: calcSessionsTooHotPotDuration(windowSessions),
        Fire: calcSessionFireCount(windowSessions),
      });
    }

    setTableData(result);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableUpdateFlag,
    sensorsLoading,
    sensorSessionLoading,
    connectivityLoading,
    statechangesLoading,
    PCOstatechangesLoading,
    bootinfoLoading,
    currentPCOstatechangesLoading,
    alertsLoading,
    historySessionsLoading,
    props.startDate,
    props.endDate,
  ]);


  // Show an overlay while the command goes out, give the device a few seconds
  // to apply it, then reload so the user sees the fresh state.
  async function applyAndReload(apiCalls) {
    setPendingMsg("Sending command, please wait…");
    try {
      const calls = Array.isArray(apiCalls) ? apiCalls : [apiCalls];
      await Promise.all(calls);
    } catch (e) {
      console.error("Cooktop command failed", e);
      setPendingMsg(null);
      window.alert("Failed to send command. Please try again.");
      return;
    }
    await delay(5000);
    window.location.reload();
  }

  const postShutdown = (id) =>
    axiosClient.post(`sensors/${id}/shutdownstove`);
  const postUnlock = (id) => axiosClient.post(`sensors/${id}/unlockstove`);

  const handleTurnOffStove = (sensorid) => applyAndReload(postShutdown(sensorid));
  const handleStoveRemoteUnlock = (sensorid) =>
    applyAndReload(postUnlock(sensorid));

  const columns = React.useMemo(
    () => [
      {
        Header: "Sensor",
        accessor: "UnitNumber",
        filter: "fuzzyText",
        textCell: true,
        isVisible: true,
      },
      {
        Header: "Priority",
        accessor: "PriorityScore",
        description:
          "How problematic this sensor is right now, based on current danger and how fast it's worsening.",
        sortType: (a, b) => {
          const x = a.original.PriorityScore ?? -Infinity;
          const y = b.original.PriorityScore ?? -Infinity;
          return x === y ? 0 : x > y ? 1 : -1;
        },
        Cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <PriorityBadge
              score={row.original.PriorityScore}
              band={row.original.PriorityBand}
            />
          </div>
        ),
        isVisible: true,
      },
      {
        Header: "Trend",
        accessor: "TrendRank",
        description:
          "Safety trend over the last 4 weeks vs the prior 4 weeks (alerts, unattended cooking, safety shut offs, and behavior).",
        Cell: ({ row }) => (
          <div
            className="flex items-center justify-center"
            title="Safety trend: last 4 weeks vs the prior 4 weeks."
          >
            <TrendBadge
              level={row.original.TrendLevel}
              status={row.original.Trend || "stable"}
            />
          </div>
        ),
        isVisible: true,
      },
      {
        Header: "Cooktop State",
        accessor: "StoveState",
        description:
          "Live sensor and cooktop state. Sorted by severity: offline first, then locked, then disabled, then enabled.",
        sortType: (a, b) => {
          // Live cooking / temperature come from a per-row MQTT probe, so
          // we cannot include them here. Rank on what the row already has.
          const rank = (r) => {
            if (r.Status !== "Online") return 0; // offline (top)
            if (r.pcoStateReduced === "LOCK") return 1;
            if (r.pcoStateReduced === "OFF") return 2;
            if (r.pcoStateReduced === "ON") return 3;
            return 4; // online, no PCO / unknown (bottom)
          };
          const x = rank(a.original);
          const y = rank(b.original);
          return x === y ? 0 : x > y ? 1 : -1;
        },
        Cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <StoveStateBadge
              sensorName={row.original.SensorID}
              isOnline={row.original.Status === "Online"}
              pcoState={row.original.pcoStateReduced}
              unit={tempUnit}
              offlineSinceTs={row.original.OfflineSinceTs}
            />
          </div>
        ),
        isVisible: true,
      },
      {
        Header: "Cooktop Control",
        accessor: "StoveControl",
        disableSortBy: true,
        Cell: ({ row }) => {
          const ctrl = deriveStoveControl(row.original);
          const onAction = () => {
            if (ctrl.action === "shutoff") {
              if (
                window.confirm(
                  `Disable and lock ${row.original.UnitNumber}?`
                )
              ) {
                handleTurnOffStove(row.original.SensorID);
              }
            } else if (ctrl.action === "unlock") {
              if (
                window.confirm(`Unlock ${row.original.UnitNumber}?`)
              ) {
                handleStoveRemoteUnlock(row.original.SensorID);
              }
            } else if (ctrl.action === "lock") {
              if (
                window.confirm(
                  `Lock ${row.original.UnitNumber}?`
                )
              ) {
                handleTurnOffStove(row.original.SensorID);
              }
            }
          };
          return (
            <div
              className="flex items-center justify-center"
              title={ctrl.tooltip}
            >
              {ctrl.action ? (
                <Button
                  size="md"
                  variant={ctrl.action === "shutoff" ? "danger" : "info"}
                  leadingIcon={
                    ctrl.action === "unlock" ? (
                      <FaUnlock size={14} />
                    ) : ctrl.action === "lock" ? (
                      <FaLock size={14} />
                    ) : (
                      <LuPowerOff size={16} />
                    )
                  }
                  onClick={onAction}
                  className="!rounded-control !border-black whitespace-nowrap shadow-sm"
                >
                  {ctrl.actionLabel}
                </Button>
              ) : (
                <StatusDot tone={ctrl.tone} label={ctrl.label} />
              )}
            </div>
          );
        },
        isVisible: true,
      },
      {
        Header: "Cooking Sessions",
        accessor: "NumberOfSessions",
        description: "Cooking sessions in the selected date range.",
        Cell: ({ row }) => (
          <div
            className="text-center"
            title="Cooking sessions in the selected date range."
          >
            {row.original.NumberOfSessions ?? 0}
          </div>
        ),
        isVisible: true,
      },
      {
        Header: "Last Session",
        accessor: "LastSessionTs",
        description: "Most recent cooking session ever recorded.",
        sortType: (a, b) => {
          const x = a.original.LastSessionTs ?? -Infinity;
          const y = b.original.LastSessionTs ?? -Infinity;
          return x === y ? 0 : x > y ? 1 : -1;
        },
        Cell: ({ row }) => {
          const ts = row.original.LastSessionTs;
          // Flag long gaps: warn at 5+ days without a cooking session, red at
          // 10+ days. Cooking is naturally less frequent than presence so the
          // thresholds are looser than for Last Presence.
          const tone = stalenessColor(ts, { warnDays: 5, criticalDays: 10 });
          return (
            <div
              className={`text-center ${tone}`}
              title={
                ts == null
                  ? "Never"
                  : `${new Date(ts).toLocaleString()}${
                      tone.includes("alert-critical")
                        ? " — long gap, consider a wellness check"
                        : tone.includes("alert-warning")
                          ? " — longer than usual"
                          : ""
                    }`
              }
            >
              {formatRelativeShort(ts)}
            </div>
          );
        },
        isVisible: true,
      },
      {
        Header: "Last Presence",
        accessor: "LastPresenceTs",
        description: "Most recent time presence was detected.",
        sortType: (a, b) => {
          const x = a.original.LastPresenceTs ?? -Infinity;
          const y = b.original.LastPresenceTs ?? -Infinity;
          return x === y ? 0 : x > y ? 1 : -1;
        },
        Cell: ({ row }) => {
          const ts = row.original.LastPresenceTs;
          // Presence is expected daily; a few days without motion near the
          // cooktop is a real wellness signal. Warn at 2 days, red at 5.
          const tone = stalenessColor(ts, { warnDays: 2, criticalDays: 5 });
          return (
            <div
              className={`text-center ${tone}`}
              title={
                ts == null
                  ? "Never"
                  : `${new Date(ts).toLocaleString()}${
                      tone.includes("alert-critical")
                        ? " — no motion near the cooktop in days, consider a wellness check"
                        : tone.includes("alert-warning")
                          ? " — longer than usual"
                          : ""
                    }`
              }
            >
              {formatRelativeShort(ts)}
            </div>
          );
        },
        isVisible: true,
      },
      {
        Header: "Alerts",
        accessor: "Alerts",
        description: "Danger alerts in the selected date range.",
        Cell: ({ row }) => {
          const v = row.original.Alerts ?? 0;
          return (
            <div
              className={`text-center font-semibold ${alertCountColor(v)}`}
              style={alertCountStyle(v)}
              title="Danger alerts in the selected date range."
            >
              {v}
            </div>
          );
        },
        isVisible: true,
      },
      {
        Header: "Safety\nShut Offs",
        accessor: "Shutdowns",
        description: "Safety shut offs in the selected date range.",
        Cell: ({ row }) => {
          // Shutdowns are historical (from alarm records) and the PCO's presence
          // is known from its last reported state, so this stays meaningful even
          // when the sensor is currently offline. N/A only means "no PCO ever".
          const hasPCO = row.original.isPCOconnected === "YES";
          if (!hasPCO) {
            return (
              <div
                className="text-center font-semibold text-ink-3"
                title="No power cutoff unit on this sensor."
              >
                N/A
              </div>
            );
          }
          const v = row.original.Shutdowns ?? 0;
          return (
            <div
              className={`text-center font-semibold ${
                v > 0 ? "text-alert-info" : "text-ink-3"
              }`}
              title="Times the cooktop was automatically shut off in response to a danger alert during this window."
            >
              {v}
            </div>
          );
        },
        isVisible: true,
      },
    ],
    [tempUnit]
  );

  const filterTypes = React.useMemo(
    () => ({
      text: (rows, id, filterValue) =>
        rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        }),
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: TableData,
      initialState: {
        hiddenColumns: columns
          .filter((column) => !column.isVisible)
          .map((column) => column.accessor),
        sortBy: [{ id: "PriorityScore", desc: true }],
        pageIndex: (() => {
          try {
            const raw = sessionStorage.getItem(
              `sensors-table-page:${props.building.buildingId}`
            );
            const n = raw ? parseInt(raw, 10) : 0;
            return Number.isFinite(n) && n >= 0 ? n : 0;
          } catch {
            return 0;
          }
        })(),
        pageSize: 10,
      },
      filterTypes,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // Apply search from page header.
  useEffect(() => {
    setGlobalFilter(props.searchQuery || undefined);
  }, [props.searchQuery, setGlobalFilter]);

  // Persist current page across navigation to the sensor detail and back.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        `sensors-table-page:${props.building.buildingId}`,
        String(pageIndex)
      );
    } catch {
      /* storage unavailable */
    }
  }, [pageIndex, props.building.buildingId]);

  // Clamp when the row set shrinks (search filter, deleted sensor, restored
  // page beyond the new pageCount). Without this, react-table slices an
  // out-of-range page and the table renders blank with no way to recover.
  useEffect(() => {
    if (pageCount > 0 && pageIndex > pageCount - 1) {
      gotoPage(Math.max(0, pageCount - 1));
    }
  }, [pageCount, pageIndex, gotoPage]);

  // Pad page rows for constant table height.
  const paddedPage = React.useMemo(() => {
    const padded = [...page];
    while (padded.length < pageSize) padded.push(null);
    return padded;
  }, [page, pageSize]);

  const handleQuickAction = async (label) => {
    if (label === "refresh") {
      setLoading(true);
      await delay(800);
      setTableUpdateFlag((f) => !f);
    } else if (label === "powerOffAll") {
      const targets = TableData.filter(
        (item) =>
          item.Status === "Online" &&
          item.isPCOconnected === "YES" &&
          item.stoveStatus === "ON"
      );
      if (targets.length === 0) return;
      if (
        !window.confirm(
          `Disable and lock ${targets.length} cooktop${
            targets.length === 1 ? "" : "s"
          } now?`
        )
      ) {
        return;
      }
      applyAndReload(targets.map((t) => postShutdown(t.SensorID)));
    } else if (label === "unlockAll") {
      const targets = TableData.filter(
        (item) =>
          item.Status === "Online" &&
          item.isPCOconnected === "YES" &&
          item.stoveStatus === "OFF" &&
          item.IsStoveRemoteUnlocked === "LOCKED"
      );
      if (targets.length === 0) return;
      if (
        !window.confirm(
          `Unlock ${targets.length} cooktop${
            targets.length === 1 ? "" : "s"
          }?`
        )
      ) {
        return;
      }
      applyAndReload(targets.map((t) => postUnlock(t.SensorID)));
    }
  };

  const totalAlerts = TableData.reduce(
    (acc, item) => acc + (Number(item.Alerts) || 0),
    0
  );
  const totalOffline = TableData.reduce(
    (acc, item) => acc + (item.Status === "Offline" ? 1 : 0),
    0
  );
  const totalStovesOff = TableData.reduce(
    (acc, item) =>
      acc +
      (item.stoveStatus === "OFF" &&
      item.PCOFWVersion !== "NULL" &&
      item.Status === "Online"
        ? 1
        : 0),
    0
  );
  const totalSessions = TableData.reduce(
    (acc, item) => acc + (Number(item.NumberOfSessions) || 0),
    0
  );
  const totalShutdowns = TableData.reduce(
    (acc, item) =>
      acc + (item.PCOFWVersion !== "NULL" ? item.Shutdowns ?? 0 : 0),
    0
  );
  const totalLocked = TableData.reduce(
    (acc, item) =>
      acc +
      (item.Status === "Online" &&
      item.isPCOconnected === "YES" &&
      item.stoveStatus === "OFF" &&
      item.IsStoveRemoteUnlocked === "LOCKED"
        ? 1
        : 0),
    0
  );
  // If no sensor currently has stove control available (either no PCO ever
  // installed, or every sensor with one is offline), the power/lock KPIs are
  // meaningless, so surface "N/A" rather than a misleading 0.
  const anyControllable = TableData.some(
    (item) => item.Status === "Online" && item.isPCOconnected === "YES"
  );

  // Schedule fan-out targets: every PCO-equipped sensor (online or not).
  const scheduleTargets = TableData.filter(
    (item) => item.isPCOconnected === "YES"
  ).map((item) => ({ name: item.SensorID, displayName: item.UnitNumber }));

  if (error) {
    return (
      <ErrorState
        title="Couldn't load sensor data"
        message="One or more sensor telemetry queries failed. Please retry."
        onRetry={() => setTableUpdateFlag((f) => !f)}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={96} />
      </div>
    );
  }

  if (!TableData || TableData.length === 0) {
    return (
      <div className="rounded-card border border-dashed border-white/25 p-10 text-center bg-white/5 max-w-2xl mx-auto">
        <h2 className="text-xl font-semibold text-white">
          No sensors found in {props.building.name || "this property"}
        </h2>
        <p className="mt-2 text-ink-3">
          Install a CTS Smart Kitchen Sensor with the phone app and refresh this page.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-2">
      {pendingMsg && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-card p-6 text-ink shadow-card flex items-center gap-3 max-w-md">
            <Spinner size={32} />
            <span className="text-sm">{pendingMsg}</span>
          </div>
        </div>
      )}
      <div className="container-infobox">
        <div className="info-textbox">
          <IconContext.Provider
            value={{ color: "#7C4DFF", className: "icon" }}
          >
            <IoCloudOffline className="icon" />
          </IconContext.Provider>
          <div className="info-textbox-body">
            <span>{totalOffline}</span>
            <h2>Offline</h2>
          </div>
        </div>
        <div className="info-textbox">
          <IconContext.Provider
            value={{ color: "#B00020", className: "icon" }}
          >
            <LuPower className="icon" />
          </IconContext.Provider>
          <div className="info-textbox-body">
            <span>{anyControllable ? totalStovesOff : "N/A"}</span>
            <h2>Disabled</h2>
          </div>
        </div>
        <div className="info-textbox">
          <IconContext.Provider
            value={{ color: "#1976D2", className: "icon" }}
          >
            <FaLock className="icon" />
          </IconContext.Provider>
          <div className="info-textbox-body">
            <span>{anyControllable ? totalLocked : "N/A"}</span>
            <h2>Locked</h2>
          </div>
        </div>
        <div className="info-textbox">
          <IconContext.Provider
            value={{ color: "#BBDA62", className: "icon" }}
          >
            <PiCookingPotFill className="icon" />
          </IconContext.Provider>
          <div className="info-textbox-body">
            <span>{totalSessions}</span>
            <h2>Cooking sessions</h2>
          </div>
        </div>
        <div className="info-textbox">
          <IconContext.Provider
            value={{ color: "#E74C3C", className: "icon" }}
          >
            <IoNotifications className="icon" />
          </IconContext.Provider>
          <div className="info-textbox-body">
            <span>{totalAlerts}</span>
            <h2>Alerts</h2>
          </div>
        </div>
        <div
          className="info-textbox"
          title={
            anyControllable
              ? "Times the cooktop was automatically shut off in response to a danger alert during this window."
              : "No sensors at this property currently have a power cutoff unit available."
          }
        >
          <IconContext.Provider
            value={{ color: "#1976D2", className: "icon" }}
          >
            <LuPowerOff className="icon" />
          </IconContext.Provider>
          <div className="info-textbox-body">
            <span>{anyControllable ? totalShutdowns : "N/A"}</span>
            <h2>Safety shut offs</h2>
          </div>
        </div>
      </div>

      <SensorAttention rows={TableData} onSensorClick={props.onSensorClick} />

      {(() => {
        const cookingCount = TableData.filter(
          (i) =>
            i.Status === "Online" &&
            i.isPCOconnected === "YES" &&
            i.stoveStatus === "ON"
        ).length;
        const lockedCount = TableData.filter(
          (i) =>
            i.Status === "Online" &&
            i.isPCOconnected === "YES" &&
            i.stoveStatus === "OFF" &&
            i.IsStoveRemoteUnlocked === "LOCKED"
        ).length;
        return (
          <div className="cts-action-bar">
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<IoMdRefreshCircle size={20} />}
              onClick={() => handleQuickAction("refresh")}
            >
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<LuCirclePower size={18} />}
              onClick={() => handleQuickAction("powerOffAll")}
              disabled={cookingCount === 0}
              title={
                cookingCount === 0
                  ? "No cooktops are currently on."
                  : `Disable and lock ${cookingCount} cooktop${
                      cookingCount === 1 ? "" : "s"
                    }`
              }
            >
              Disable &amp; Lock All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<FaUnlock size={16} />}
              onClick={() => handleQuickAction("unlockAll")}
              disabled={lockedCount === 0}
              title={
                lockedCount === 0
                  ? "No cooktops are currently locked."
                  : `Unlock ${lockedCount} cooktop${
                      lockedCount === 1 ? "" : "s"
                    }`
              }
            >
              Unlock All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<FaRegCalendarAlt size={16} />}
              onClick={() => setScheduleModalOpen(true)}
              disabled={scheduleTargets.length === 0}
              title={
                scheduleTargets.length === 0
                  ? "No power cutoff sensors at this property."
                  : `Set a schedule for ${scheduleTargets.length} power-cutoff sensor${
                      scheduleTargets.length === 1 ? "" : "s"
                    }`
              }
            >
              Schedule for All
            </Button>
          </div>
        );
      })()}

      <div>
        <div className="cts-table-card overflow-x-auto">
          <table className="cts-table" {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key: hgKey, ...hgRest } =
                  headerGroup.getHeaderGroupProps();
                return (
                  <tr key={hgKey} {...hgRest}>
                    {headerGroup.headers.map((column) => {
                      const sortState = column.isSorted
                        ? column.isSortedDesc
                          ? "desc"
                          : "asc"
                        : "none";
                      const desc = column.description
                        ? `: ${column.description}`
                        : "";
                      const { key: colKey, ...colRest } = column.getHeaderProps(
                        column.canSort
                          ? column.getSortByToggleProps({
                              title: `Sort by ${column.render("Header")}${desc}`,
                            })
                          : {
                              style: { cursor: "default" },
                              title: column.description,
                            }
                      );
                      return (
                        <th key={colKey} {...colRest}>
                          <span className="inline-flex items-center justify-center">
                            {column.render("Header")}
                            {column.canSort ? (
                              <span className="cts-sort">
                                <SortIndicator state={sortState} />
                              </span>
                            ) : null}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {paddedPage.map((row, idx) => {
                if (!row) {
                  return (
                    <tr key={`empty-${idx}`} className="cts-empty-row">
                      <td colSpan={columns.length}>&nbsp;</td>
                    </tr>
                  );
                }
                prepareRow(row);
                const { key: rowKey, ...rowRest } = row.getRowProps();
                return (
                  <tr
                    key={rowKey}
                    {...rowRest}
                    onClick={(e) => {
                      // Don't drill in when clicking the inline action buttons
                      if (
                        e.target.closest("button") ||
                        e.target.closest("a")
                      ) {
                        return;
                      }
                      if (props.onSensorClick) {
                        props.onSensorClick({
                          name: row.original.SensorID,
                          displayName: row.original.UnitNumber,
                        });
                      }
                    }}
                  >
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellRest } = cell.getCellProps();
                      const textCellClass = cell.column.textCell
                        ? "cts-text-cell"
                        : "";
                      return (
                        <td
                          key={cellKey}
                          {...cellRest}
                          className={textCellClass}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {pageCount > 1 && (
            <div className="cts-pagination">
              <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                {"«"}
              </button>
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                {"‹"}
              </button>
              {Array.from(
                { length: Math.min(5, pageCount - pageIndex) },
                (_, i) => {
                  const pageNumber = pageIndex + 1 + i;
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => gotoPage(pageNumber - 1)}
                      style={
                        pageNumber - 1 === pageIndex
                          ? {
                              background: "rgba(187,218,98,0.2)",
                              borderColor: "#BBDA62",
                            }
                          : undefined
                      }
                    >
                      {pageNumber}
                    </button>
                  );
                }
              )}
              <button onClick={() => nextPage()} disabled={!canNextPage}>
                {"›"}
              </button>
              <button
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              >
                {"»"}
              </button>
            </div>
          )}
        </div>
      </div>

      <BuildingScheduleModal
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        buildingName={props.building.name}
        targets={scheduleTargets}
      />
    </div>
  );
}
